export type DateOfBirthValidationResult = {
	isValid: boolean;
	value?: string | null;
	error?: string;
};

/**
 * HI_005_005808
 * HI_010_005808
 *
 * Patient DOB must be a full date with:
 * - day
 * - month
 * - exactly four-digit year
 *
 * Canonical value sent to backend: YYYY-MM-DD
 */
export const FULL_DOB_REGEX = /^\d{4}-\d{2}-\d{2}$/;

export const normaliseDateOfBirth = (
	value: string | Date | null | undefined
): string | null => {
	if (value === null || value === undefined || value === "") {
		return null;
	}

	if (value instanceof Date) {
		if (Number.isNaN(value.getTime())) {
			return "";
		}

		const year = value.getFullYear();
		const month = String(value.getMonth() + 1).padStart(2, "0");
		const day = String(value.getDate()).padStart(2, "0");

		return `${year}-${month}-${day}`;
	}

	if (typeof value === "string") {
		// Already canonical date-only string.
		if (FULL_DOB_REGEX.test(value)) {
			return value;
		}

		// Handle ISO string accidentally created by JSON serialising a JS Date.
		const isoDateOnly = value.match(/^(\d{4}-\d{2}-\d{2})T/);
		if (isoDateOnly) {
			return isoDateOnly[1];
		}

		return value;
	}

	return "";
};

/**
 * CU-14ygtkj65jv — apply the full-DOB contract to a request payload's
 * `date_of_birth`, returning a copy carrying the canonical `YYYY-MM-DD` value.
 *
 * Presence, not truthiness: a payload without the key is returned untouched, so
 * an omitted DOB stays omitted rather than becoming an explicit clear. An
 * explicit null stays null. An invalid value THROWS rather than being repaired
 * into a different date.
 *
 * This is the one place the operation lives. `PatientApi` uses it for the
 * canonical practitioner routes and `userApi` uses it for the generic
 * patient-target routes, so both produce the same representation from the same
 * rule rather than two drifting copies.
 */
export const withNormalisedDateOfBirth = <T extends { date_of_birth?: unknown }>(
	payload: T
): T => {
	if (!("date_of_birth" in payload)) {
		return payload;
	}

	const validation = validateFullDateOfBirth(payload.date_of_birth as string | Date | null | undefined);

	if (!validation.isValid) {
		throw new Error(validation.error || "Invalid date of birth.");
	}

	return { ...payload, date_of_birth: validation.value };
};

export const validateFullDateOfBirth = (
	value: string | Date | null | undefined
): DateOfBirthValidationResult => {
	const normalised = normaliseDateOfBirth(value);

	if (normalised === null) {
		return { isValid: true, value: null };
	}

	if (!FULL_DOB_REGEX.test(normalised)) {
		return {
			isValid: false,
			error: "Date of birth must include day, month and a four-digit year.",
		};
	}

	const [yearText, monthText, dayText] = normalised.split("-");
	const year = Number(yearText);
	const month = Number(monthText);
	const day = Number(dayText);

	const parsed = new Date(year, month - 1, day);

	const isRealDate =
		parsed.getFullYear() === year &&
		parsed.getMonth() === month - 1 &&
		parsed.getDate() === day;

	if (!isRealDate) {
		return {
			isValid: false,
			error: "Date of birth must be a valid calendar date.",
		};
	}

	return { isValid: true, value: normalised };
};